import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import VectorSource from 'ol/source/Vector';
import { Fill, Style } from 'ol/style';
import { VectorLayer } from 'os-elements';
import { mapModes } from 'client/utils/constants/appStates';
import { LAYER_CONFIG } from 'client/pages/MapPage/layers/constants';
import { buildServiceAreaGeometry, buildServiceAreaMask } from 'client/utils/map/serviceAreaUtils';
import { selectMyCoverage } from 'client/store/coverageAssetOwnersSlice/coverageAssetOwnersSlice';

const { DRAWING, MODIFYING } = mapModes;

const MASK_FILL_COLOR = 'rgba(0, 0, 0, 0.3)';

// Fills the world-extent rectangle with semi-transparent grey.
const greyFillStyle = new Style({
    fill: new Fill({ color: MASK_FILL_COLOR })
});

/**
 * Cuts the service area out of the grey overlay so it appears clear.
 * Uses a custom renderer because we need Canvas 'destination-out' to
 * erase the grey fill where the user is allowed to draw.
 */
const cutoutStyle = new Style({
    renderer: (coordinates, renderState) => {
        const ctx = renderState.context;
        if (!ctx) {
            return;
        }

        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = 'rgba(0, 0, 0, 1)';
        ctx.beginPath();

        for (const polygon of coordinates) {
            for (const ring of polygon) {
                ctx.moveTo(ring[0][0], ring[0][1]);
                for (let i = 1; i < ring.length; i++) {
                    ctx.lineTo(ring[i][0], ring[i][1]);
                }
                ctx.closePath();
            }
        }

        ctx.fill();
        ctx.restore();
    }
});

const ServiceAreaOverlay = ({ epsgCode }) => {
    const mapMode = useSelector(state => state.map?.mode);
    const myCoverage = useSelector(selectMyCoverage, shallowEqual);

    const isDrawOrModify = mapMode?.includes(DRAWING) || mapMode?.includes(MODIFYING);

    const maskSource = useMemo(() => {
        if (!myCoverage?.serviceAreas?.length) {
            return null;
        }

        const serviceAreaGeometry = buildServiceAreaGeometry(myCoverage.serviceAreas);
        const mask = buildServiceAreaMask(serviceAreaGeometry, epsgCode);

        if (!mask) {
            return null;
        }

        // Styles must be set on each feature (not on the layer) because
        // os-elements does not reliably apply layer-level style objects.
        mask.worldFeature.setStyle(greyFillStyle);
        mask.serviceAreaFeature.setStyle(cutoutStyle);

        return new VectorSource({
            features: [mask.worldFeature, mask.serviceAreaFeature]
        });
    }, [myCoverage, epsgCode]);

    if (!maskSource) {
        return null;
    }

    return (
        // className gives this layer its own Canvas so the 'destination-out'
        // composite operation only erases within this layer, not the base map.
        <VectorLayer
            id={LAYER_CONFIG.serviceAreaOverlay.id}
            visible={isDrawOrModify}
            source={maskSource}
            className="service-area-overlay"
            zIndex={LAYER_CONFIG.serviceAreaOverlay.zIndex}
            data-testid="serviceAreaOverlay"
        />
    );
};

ServiceAreaOverlay.propTypes = {
    epsgCode: PropTypes.string
};

export default ServiceAreaOverlay;
